import { defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, unref as _unref, toDisplayString as _toDisplayString, resolveComponent as _resolveComponent, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, createSlots as _createSlots } from "vue"

const _hoisted_1 = { class: "pb-xs" }
const _hoisted_2 = { class: "flex justify-end items-end" }
const _hoisted_3 = { class: "ml-xs mr-xl block" }
const _hoisted_4 = { class: "ml-xs mr-xl block" }
const _hoisted_5 = { class: "m-0 text-gray-600" }

import { computed, ref, watch } from "vue";
import { Message, OrderMedication } from "@/interfaces/message";
import IconButton from "@/components/IconButton.vue";
import { useOrdersStore } from "@/store/ordersStore";
import UmanaHeadline from "@/components/UmanaHeadline.vue";
import ListTile from "@/components/ListTile.vue";
import CoreIcon from "@/components/CoreIcon.vue";
import { patientString } from "@/helpers/patienthelper";
import { useManagedPatientsStore } from "@/store/managedPatientsStore";
import { useSessionStore } from "@/store/sessionStore";
import { bytesToHex, encryptMsg } from "@/helpers/encryption";
import { supabase } from "@/helpers/supabase";
import { useNotificationStore } from "@/store/notificationStore";
import { Medication } from "@/interfaces/medication";
import {
  findMedicationWithGtin,
  findMedicationWithPharmacode,
} from "@/helpers/requestHelpers/medicationRequests";
import NincdString from "@/components/NincdString.vue";
import EmptyContainer from "@/components/EmptyContainer.vue";
import SmallTag from "@/components/SmallTag.vue";
import { usePublicStore } from "@/store/publicStore";
import { Conversation } from "@/interfaces/conversation";
import MedicationInformation from "@/components/MedicationInformation.vue";
import { StorageLocation } from "@/interfaces/enum/StorageLocation";
import { useMedicationsStore } from "@/store/medicationsStore";


export default /*#__PURE__*/_defineComponent({
  __name: 'OrderMedicationFavorites',
  props: {
    excludeOrderId: {},
    hidePlus: { type: Boolean },
    showSearch: { type: Boolean },
    filterPatients: {},
    conversation: {}
  },
  emits: ["close", "addMedication"],
  setup(__props: any, { emit: __emit }) {

const storageLocation = StorageLocation;

const sessionStore = useSessionStore();
const publicStore = usePublicStore();
const medicationsStore = useMedicationsStore();

const emits = __emit;

const props = __props;

const medication = ref<Medication | null>();
const favoriteMedicationSearchString = ref<string>("");
const ordersSearchString = ref<string>("");

const shops = computed(() => {
  return (
    publicStore.careProviderGroups
      ?.filter((cpg) => cpg.is_shop)
      .map((cpg) => ({ label: cpg.group_name, value: cpg.id })) ?? [
      { label: "Keine Apotheke zur Auswahl", value: null },
    ]
  );
});

const selectedShop = ref(
  props.conversation
    ? props.conversation.receiverCareProviderGroup.id
    : sessionStore.selectedCareProviderGroup?.is_shop
    ? sessionStore.selectedCareProviderGroup?.id
    : sessionStore.selectedCareProviderGroup?.standard_shop ??
      shops.value[0].value
);

const isLoadingMedicationInformation = ref(true);
const errorLoadingMedicationInformation = ref(false);

const fetchMedicationsInformation = (selectedShop: number | null) => {
  sessionStore
    .fetchMedicationsInformation(selectedShop)
    .catch(() => {
      errorLoadingMedicationInformation.value = true;
    })
    .finally(() => {
      isLoadingMedicationInformation.value = false;
    });
};

fetchMedicationsInformation(selectedShop.value);

watch(selectedShop, (newValue) => {
  fetchMedicationsInformation(newValue);
});

const allOrders = computed(() =>
  Array.from(useOrdersStore().messages?.values() ?? []).filter(
    (order) =>
      order.medication_order &&
      (!props.excludeOrderId ||
        order.conversation.id != props.excludeOrderId) &&
      hasPatient(order.medication_order.patient_id)
  )
);

const filteredOrders = computed(() => {
  return (
    allOrders.value.filter(
      (m) =>
        m.conversation.receiver_care_provider_group_id === selectedShop.value &&
        m.medication_order?.gtin !== 0 &&
        `${m.medication_order?.pharmacode}${m.medication_order?.name}${m.medication_order?.gtin}`
          .toLocaleLowerCase()
          .includes(ordersSearchString.value.toLocaleLowerCase())
    ) ?? []
  );
});

const filteredFavorites = computed(() => {
  return (
    sessionStore.favorites?.filter(
      (m) =>
        m.care_provider_group_id === selectedShop.value &&
        `${m.pharmacode}${m.dscrd}${m.gtin}`
          .toLocaleLowerCase()
          .includes(favoriteMedicationSearchString.value.toLocaleLowerCase())
    ) ?? []
  );
});

async function addFavorite(product: Medication) {
  if (product && product.pharmacode) {
    // catch reset state
    await toggleFavorite(product, false);
    medication.value = null;
  }
}

function hasPatient(patientId: number): boolean {
  if (patientId === 0) {
    return true;
  }
  if (!props.filterPatients) {
    if (
      useManagedPatientsStore().patients?.find(
        (patient) => patient.id == patientId
      )
    ) {
      return true;
    }
    return false;
  }

  if (
    props.filterPatients.findIndex((patient) => patient.value == patientId) !=
    -1
  ) {
    return true;
  }
  return false;
}

function jsonstringToMedicationFormJson(medicationstring: string) {
  let json = JSON.parse(medicationstring) as OrderMedication;
  json.medicationObject = {
    pharmacode: json.pharmacode,
    gtin: json.gtin,
    dscrd: json.name,
    nincd: json.nincd ?? -2,
    public_price: json.public_price,
    storage_quantity: json.storage_quantity,
  };
  return json;
}

async function copy(order: Message) {
  if (order.decrypted_message) {
    const medication = jsonstringToMedicationFormJson(order.decrypted_message);
    await medicationsStore.fetchMedicationInformation(
      medication,
      props.conversation?.receiverCareProviderGroup.id,
      true
    );
    emits("addMedication", medication);
  }
}

async function orderProduct(product: Medication) {
  await medicationsStore.fetchMedicationInformation(
    product,
    props.conversation?.receiverCareProviderGroup.id,
    true
  );
  emits("addMedication", product);
}

async function toggleFavoriteFromOrder(orderMedication: OrderMedication) {
  if (selectedShop.value) {
    let medication: Medication | null = null;
    if (orderMedication.pharmacode) {
      const res = await findMedicationWithPharmacode(
        orderMedication.pharmacode,
        selectedShop.value
      );
      medication = res.data;
    } else if (orderMedication.gtin) {
      const res = await findMedicationWithGtin(
        orderMedication.gtin,
        selectedShop.value
      );
      medication = res.data;
    }
    if (medication) {
      medication.isInIndex = true;
      await toggleFavorite(medication);
    } else {
      useNotificationStore().notifications.push({
        msg: `${orderMedication.name} kann nicht zur Favoritenliste hinzugefügt werden, weil das Medikament nicht mehr existiert.`,
        type: "danger",
      });
    }
  }
}

function isFavorite(
  pharmacode: number | undefined,
  gtin: number | undefined | null
) {
  if (pharmacode) {
    return (
      (filteredFavorites.value ?? []).findIndex(
        (m) => m.pharmacode == pharmacode
      ) > -1
    );
  } else {
    return (
      (filteredFavorites.value ?? []).findIndex((m) => m.gtin == gtin) > -1
    );
  }
}

async function toggleFavorite(medication: Medication, shouldToggle = true) {
  let successText = "zu Favoriten hinzugefügt";
  if (
    sessionStore.selectedCareProviderGroup != null &&
    sessionStore.favorites != null &&
    medication.pharmacode
  ) {
    try {
      if (isFavorite(medication.pharmacode, medication.gtin)) {
        if (shouldToggle) {
          sessionStore.favorites = sessionStore.favorites.filter(
            (m) => m.pharmacode != medication.pharmacode
          );
          successText = "von Favoriten entfernt";
        } else {
          useNotificationStore().removeNotificationsWithId("favorites");
          useNotificationStore().notifications.push({
            msg: `${medication.dscrd} ist bereits in der Favoritenliste`,
            type: "warning",
            customId: "favorites",
          });
          return;
        }
      } else {
        medication.isInIndex = true;
        sessionStore.favorites.push(medication);
      }

      let encryptedObj = await encryptMsg({
        message: JSON.stringify(sessionStore.favorites),
        serverCareProviderGroupId: sessionStore.selectedCareProviderGroup.id,
        clientCareProviderGroupId: sessionStore.selectedCareProviderGroup.id,
        creatorCareProviderGroupId: sessionStore.selectedCareProviderGroup.id,
      });
      if (encryptedObj.clientKeyId != encryptedObj.serverKeyId) {
        alert("Keys sind nicht synchron. Fehler.");
        return;
      }

      await supabase.from("careProviderGroup_favorites").upsert({
        nonce: bytesToHex(encryptedObj.nonce),
        encrypted_favorites: bytesToHex(encryptedObj.encryptedMsg),
        used_key: encryptedObj.clientKeyId,
        care_provider_group_id: sessionStore.selectedCareProviderGroup.id,
      });
      useNotificationStore().removeNotificationsWithId("favorites");
      useNotificationStore().notifications.push({
        msg: `${medication.dscrd} ${successText}`,
        type: "success",
        customId: "favorites",
      });
    } catch (e) {
      sessionStore.favorites = sessionStore.favorites.filter(
        (m) => m.pharmacode != medication.pharmacode
      );

      return;
    }
  }
}

return (_ctx: any,_cache: any) => {
  const _component_FormKit = _resolveComponent("FormKit")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createVNode(SmallTag, {
        small: true,
        color: "blue"
      }, {
        default: _withCtx(() => [
          _createTextVNode("Beta")
        ]),
        _: 1
      })
    ]),
    _createVNode(UmanaHeadline, { h: "h2" }, {
      default: _withCtx(() => [
        _createTextVNode("Meine Favoriten " + _toDisplayString(props.conversation && _unref(sessionStore).selectedCareProviderGroup?.is_orderer
          ? `für ${props.conversation.receiverCareProviderGroup.group_name}`
          : ""), 1)
      ]),
      _: 1
    }),
    (
        _unref(sessionStore).selectedCareProviderGroup?.is_orderer &&
        !props.conversation
      )
      ? (_openBlock(), _createBlock(_component_FormKit, {
          key: 0,
          modelValue: selectedShop.value,
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((selectedShop).value = $event)),
          type: "select",
          name: "supplier",
          id: "supplier",
          label: "Favoritenliste für",
          options: shops.value
        }, null, 8, ["modelValue", "options"]))
      : _createCommentVNode("", true),
    _createVNode(_component_FormKit, {
      type: "search",
      placeholder: "Favoriten durchsuchen",
      "outer-class": "!m-0 !mt-s",
      modelValue: favoriteMedicationSearchString.value,
      "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((favoriteMedicationSearchString).value = $event))
    }, null, 8, ["modelValue"]),
    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filteredFavorites.value, (medication) => {
      return (_openBlock(), _createBlock(ListTile, {
        key: medication.pharmacode ?? medication.gtin
      }, _createSlots({
        leading: _withCtx(() => [
          _createVNode(CoreIcon, {
            icon: "star-filled",
            onClick: ($event: any) => (toggleFavorite(medication))
          }, null, 8, ["onClick"])
        ]),
        main: _withCtx(() => [
          _createElementVNode("div", _hoisted_3, [
            _createElementVNode("p", null, _toDisplayString(medication.dscrd), 1),
            _createVNode(MedicationInformation, {
              medication: medication,
              "is-loading": isLoadingMedicationInformation.value,
              error: errorLoadingMedicationInformation.value,
              "as-list": true
            }, null, 8, ["medication", "is-loading", "error"])
          ])
        ]),
        _: 2
      }, [
        (!props.hidePlus && medication.isInIndex)
          ? {
              name: "trailing",
              fn: _withCtx(() => [
                _createVNode(IconButton, {
                  icon: "plus",
                  onClick: orderProduct,
                  onClickArgs: [medication]
                }, null, 8, ["onClickArgs"])
              ]),
              key: "0"
            }
          : undefined
      ]), 1024))
    }), 128)),
    (_unref(sessionStore).favorites && _unref(sessionStore).favorites.length == 0)
      ? (_openBlock(), _createBlock(EmptyContainer, { key: 1 }, {
          default: _withCtx(() => [
            _createTextVNode("Fügen Sie Produkte zu den Favoriten hinzu, um diese in Bestellungen schnell zu finden. Die Favoritenliste wird mit all Ihren Mitarbeiter:innen synchronisiert. "),
            (!props.showSearch)
              ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                  _createTextVNode(" Sie können früher bestellte Produkte zu Ihren Favoriten hinzufügen oder in den Einstellungen produkte suchen, die Sie zu Ihren Favoriten hinzufügen möchten. ")
                ], 64))
              : _createCommentVNode("", true)
          ]),
          _: 1
        }))
      : (filteredFavorites.value.length == 0)
        ? (_openBlock(), _createBlock(EmptyContainer, { key: 2 }, {
            default: _withCtx(() => [
              _createTextVNode("Keine Produkte entsprechen Ihrer Suche")
            ]),
            _: 1
          }))
        : _createCommentVNode("", true),
    (props.showSearch)
      ? (_openBlock(), _createElementBlock(_Fragment, { key: 3 }, [
          _createVNode(UmanaHeadline, { h: "h2" }, {
            default: _withCtx(() => [
              _createTextVNode("Favorit hinzufügen")
            ]),
            _: 1
          }),
          _createVNode(_component_FormKit, {
            type: "medicationselect",
            label: "Medikamentenname",
            name: "medicationObject",
            validation: "required",
            disableCustom: true,
            onInput: addFavorite,
            modelValue: medication.value,
            "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((medication).value = $event)),
            shopCareProviderGroupId: selectedShop.value
          }, null, 8, ["modelValue", "shopCareProviderGroupId"])
        ], 64))
      : _createCommentVNode("", true),
    (allOrders.value.length > 0)
      ? (_openBlock(), _createElementBlock(_Fragment, { key: 4 }, [
          _createVNode(UmanaHeadline, {
            h: "h2",
            class: "mt-l"
          }, {
            default: _withCtx(() => [
              _createTextVNode("Bestellte Produkte")
            ]),
            _: 1
          }),
          _createVNode(_component_FormKit, {
            type: "search",
            placeholder: "Bestellte Produkte durchsuchen",
            "outer-class": "!m-0 !mt-s",
            modelValue: ordersSearchString.value,
            "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((ordersSearchString).value = $event))
          }, null, 8, ["modelValue"]),
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filteredOrders.value, (order) => {
            return (_openBlock(), _createBlock(ListTile, {
              key: order.id
            }, _createSlots({
              leading: _withCtx(() => [
                (order.medication_order)
                  ? (_openBlock(), _createBlock(CoreIcon, {
                      key: 0,
                      icon: 
              isFavorite(
                order.medication_order?.pharmacode,
                order.medication_order?.gtin
              )
                ? 'star-filled'
                : 'star'
            ,
                      onClick: ($event: any) => (toggleFavoriteFromOrder(order.medication_order))
                    }, null, 8, ["icon", "onClick"]))
                  : _createCommentVNode("", true)
              ]),
              main: _withCtx(() => [
                _createElementVNode("div", _hoisted_4, [
                  _createTextVNode(_toDisplayString(order.medication_order?.amount) + "x " + _toDisplayString(order.medication_order?.name) + " ", 1),
                  _createElementVNode("p", _hoisted_5, [
                    _createTextVNode(_toDisplayString(_unref(patientString)(order.medication_order?.patient_id ?? 0)) + " ", 1),
                    _createVNode(NincdString, {
                      nincd: order.medication_order?.nincd
                    }, null, 8, ["nincd"])
                  ])
                ])
              ]),
              _: 2
            }, [
              (!props.hidePlus)
                ? {
                    name: "trailing",
                    fn: _withCtx(() => [
                      _createVNode(IconButton, {
                        icon: "plus",
                        onClick: copy,
                        onClickArgs: [order]
                      }, null, 8, ["onClickArgs"])
                    ]),
                    key: "0"
                  }
                : undefined
            ]), 1024))
          }), 128)),
          (filteredOrders.value.length == 0)
            ? (_openBlock(), _createBlock(EmptyContainer, { key: 0 }, {
                default: _withCtx(() => [
                  _createTextVNode("Keine Produkte entsprechen Ihrer Suche")
                ]),
                _: 1
              }))
            : _createCommentVNode("", true)
        ], 64))
      : _createCommentVNode("", true)
  ]))
}
}

})